import { mapGetters } from 'vuex'
import { payload as accountActionPayload } from '@/components/RouteComponents/Accounts/actionPayload'
import { payload as seriesActionPayload } from '@/components/RouteComponents/Series/actionPayload'
import { payload as locationActionPayload } from '@/components/RouteComponents/Locations/actionPayload'
import { payload as setActionPayload } from '@/components/RouteComponents/Sets/actionPayload'
import { payload as freefieldActionPayload } from '@/components/RouteComponents/Freefields/actionPayload'
import AutocompleteLocalSearch from '@/utils/helpers/Search/AutocompleteLocalSearch'

export const bpMixins = {
  methods: {
    /**
     * Sets a line's 'account' value to that of the currently selected customer's deposit account
     * @param lineNumber    The line number to update
     * @param newValue      The new prepayment value
     * @param instance      The line's component instance
     */
    setLineAcctToCustomerDepositAcct (lineNumber, newValue, instance) {
      if (!this.form.customer.value || !newValue.value) return
      const customer = this.getCustomerById(this.form.customer.value)
      let depositAcct = customer && customer.deposit_account ? this.getAccountById(customer.deposit_account) : null
      if (depositAcct && depositAcct.id) {
        instance.modifyLine('account', { label: depositAcct.code, value: depositAcct.id })
      }
    }
  },
  computed: {
    ...mapGetters({
      getCustomerById: 'getCustomerById',
      getAccountById: 'getAccountById',
      currentPeriod: 'getCurrentPeriod',
      getBankByBatchId: 'getBankByBatchId'
    }),
    /**
     * Returns the currency of the bank associated with the currently selected batch. Returns null if no batch
     * is selected or if no bank model is found.
     * @return Number || {null}
     */
    currency () {
      if (!this.form.batch.value) {
        return null
      }
      const bank = this.getBankByBatchId(this.form.batch.value)
      return bank && bank.currency ? bank.currency : null
    },
    /**
     * Returns the default deposit account associated with the bank associated with the currently selected batch.
     * Returns null if no bank model is found.
     */
    defaultDepositAccount () {
      const bank = this.form.batch.value ? this.getBankByBatchId(this.form.batch.value) : null
      const acct = bank ? this.getAccountById(bank.default_account_deposits) : null
      switch (this.form.status) {
        case 'POSTED':
        case 'CLOSED':
        case 'VOIDED':
          return null
        default:
          return acct ? { label: acct.code, value: acct.id } : { label: '', value: '' }
      }
    },
    currentCompany () {
      const { getCompanyByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getCompanyByBatchId(this.form.batch.value)) {
        return getCompanyByBatchId(this.form.batch.value).code
      }
      return ''
    },
    currentProduction () {
      const { getProductionByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getProductionByBatchId(this.form.batch.value)) {
        return getProductionByBatchId(this.form.batch.value).code
      }
      return ''
    },
    /**
     * Returns the line fields for this transaction type.
     * Note: this overrides the `lineFields` computed property set in the base Transactions mixin at @/Mixins/Transactions.js
     * Note: the currency associated with the bill applied to a given line must match the currency associated
     * with the currently selected batch's bank (see `batch` property in the header). Therefore, if no batch
     * is selected, then the `bill` line field will be disabled.
     * @return Array
     */
    lineFields () {
      return [
        {
          name: 'line_number',
          label: 'Line',
          dataType: 'number',
          fieldType: 'input',
          disabled: true,
          fieldWidth: '2%'
        },
        {
          name: 'company',
          label: 'Co',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentCompany,
          fieldWidth: '3.5%',
          hidden: true
        },
        {
          name: 'production',
          label: 'Prod',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentProduction,
          fieldWidth: '3.5%',
          hidden: true
        },
        {
          name: 'account',
          label: 'Account',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.postableAccounts, accountActionPayload).createOptions(),
          remote: false,
          required: true,
          disabled: true,
          readOnly: true,
          staticValue: this.defaultDepositAccount,
          fieldWidth: '7%',
          actionPayload: accountActionPayload
        },
        {
          name: 'account:name',
          label: 'Account Name',
          dataType: 'string',
          fieldType: 'display',
          displayVal: (accountId) => {
            const acct = this.$store.getters.getAccountById(accountId)
            return acct && acct.id ? acct.name : ''
          },
          fieldWidth: '8%',
          hidden: false
        },
        {
          name: 'bill',
          label: 'Bill',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: [],
          searchAsync: true,
          remote: true,
          readOnly: !this.currency,
          searchOpts: { endpoint: 'ars', query: { status: 'POSTED', batch__bank__currency__in: this.currency } },
          placeholder: 'Select bill',
          required: true,
          fieldWidth: '7%'
        },
        {
          name: 'prepayment',
          label: 'Prepayment',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: [],
          searchAsync: true,
          remote: true,
          searchOpts: { endpoint: 'pps', query: {} },
          placeholder: 'Select prepayment',
          fieldWidth: '7%'
        },
        {
          name: 'description',
          label: 'Description',
          dataType: 'string',
          fieldType: 'input',
          required: true,
          fieldWidth: '23%'
        },
        {
          name: 'amount_decimal',
          label: 'Amount',
          dataType: 'number',
          fieldType: 'money',
          required: true,
          fieldWidth: '9%'
        },
        {
          name: 'series',
          label: 'Series',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.series, seriesActionPayload).createOptions(),
          remote: false,
          required: true,
          readOnly: false,
          fieldWidth: '5%',
          actionPayload: seriesActionPayload
        },
        {
          name: 'location',
          label: 'Location',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.locations, locationActionPayload).createOptions(),
          remote: false,
          required: true,
          readOnly: false,
          fieldWidth: '5%',
          actionPayload: locationActionPayload
        },
        {
          name: 'set',
          label: 'Set',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.sets, setActionPayload).createOptions(),
          remote: false,
          required: true,
          readOnly: false,
          fieldWidth: '5%',
          actionPayload: setActionPayload
        },
        {
          name: 'free_field_1',
          label: 'FF 1',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '5%',
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_2',
          label: 'FF 2',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          fieldWidth: '5%',
          actionPayload: freefieldActionPayload
        }
      ]
    }
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/BpMixins.js